import * as React from "react";
import { useFormContext } from "react-hook-form";
import clsx from "clsx";
import type { SnapshotEventReminderFrequency } from "@/modules/snapshots/businessReports.types";
import { REMINDER_UNIT_OPTIONS } from "./reminderSimpleForm.constants";

interface Props {
  disabled?: boolean;
}

interface FormShape {
  sendReminderBefore: number | null;
  sendReminderBeforeFrequency: SnapshotEventReminderFrequency;
  reminderInterval: number;
  reminderFrequency: SnapshotEventReminderFrequency;
}

export const ReminderSimpleForm: React.FC<Props> = ({ disabled = false }) => {
  const methods = useFormContext<FormShape>();
  const containerClass = clsx("rounded-md text-gray-700", {
    "bg-gray-100": disabled,
    "bg-ivory-mist-200": !disabled,
  });
  const inputClass =
    "w-16 rounded border border-gray-300 px-2 py-1 text-center focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-200";
  const selectClass =
    "rounded border border-gray-300 bg-white py-1 pl-2 pr-8 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-200";

  return (
    <div className={containerClass}>
      <div className="space-y-2 rounded-md p-4">
        <div className="flex items-center gap-2">
          <label htmlFor="reminder-send-before">Send a reminder</label>
          <input
            id="reminder-send-before"
            type="number"
            min="0"
            max="365"
            disabled={disabled}
            className={inputClass}
            aria-invalid={!!methods.formState.errors.sendReminderBefore}
            aria-describedby={methods.formState.errors.sendReminderBefore ? "reminder-send-before-error" : undefined}
            {...methods.register("sendReminderBefore", {
              required: true,
              min: 0,
              max: 365,
              valueAsNumber: true,
            })}
          />
          <select
            id="send-reminder-before-frequency"
            aria-label="Reminder offset unit"
            disabled={disabled}
            className={selectClass}
            {...methods.register("sendReminderBeforeFrequency", { required: true })}
          >
            {REMINDER_UNIT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span>before event.</span>
        </div>
        {methods.formState.errors.sendReminderBefore && (
          <span id="reminder-send-before-error" role="alert" className="text-sm text-red-600">
            Please enter a valid number (0-365)
          </span>
        )}
      </div>

      <div className="space-y-2 rounded-md p-4">
        <div className="flex items-center gap-2">
          <label htmlFor="reminder-interval">Repeat the reminder every</label>
          <input
            id="reminder-interval"
            type="number"
            min="1"
            max="30"
            disabled={disabled}
            className={inputClass}
            aria-invalid={!!methods.formState.errors.reminderInterval}
            aria-describedby={methods.formState.errors.reminderInterval ? "reminder-interval-error" : undefined}
            {...methods.register("reminderInterval", {
              required: true,
              min: 1,
              max: 30,
              valueAsNumber: true,
            })}
          />
          <select
            id="reminder-frequency"
            aria-label="Reminder repeat unit"
            disabled={disabled}
            className={selectClass}
            {...methods.register("reminderFrequency", { required: true })}
          >
            {REMINDER_UNIT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span>.</span>
        </div>
        {methods.formState.errors.reminderInterval && (
          <span id="reminder-interval-error" role="alert" className="text-sm text-red-600">
            Please enter a valid number (1-30)
          </span>
        )}
      </div>
    </div>
  );
};
